import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle, Clock, Circle, Upload, CreditCard, FileText, Building, Flag } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stepIcons = [FileText, Upload, CreditCard, Clock, Building, Flag];

export default function TrackingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTracking(); }, [id]);

  const fetchTracking = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data: d } = await axios.get(`${API}/tracking/${id}`,  { headers: { Authorization: `Bearer ${token}` } });
      setData(d);
    } catch { navigate("/dashboard"); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50" ><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const app = data.application;

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50" >
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Dashboard</button>

        <div className="text-center mb-10">
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Application Tracking</h1>
          <p className="text-sm text-slate-500">{app.destination_country_name} - {app.visa_type}</p>
        </div>

        {/* Status Badge */}
        <div className="card-elevated rounded-2xl p-6 mb-8 text-center">
          <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${
            app.status === "approved" ? "bg-emerald-50 text-emerald-600" :
            app.status === "rejected" ? "bg-red-50 text-red-600" :
            "bg-blue-50 text-blue-600"
          }`}>{app.status.replace("_", " ").toUpperCase()}</span>
          <p className="text-xs text-slate-400 mt-2">Last updated: {new Date(app.updated_at).toLocaleDateString()}</p>
        </div>

        {/* Steps Timeline */}
        <div className="card-elevated rounded-2xl p-8 mb-8" data-testid="tracking-steps">
          {data.steps.map((step, i) => {
            const Icon = stepIcons[i] || Circle;
            const isCompleted = step.status === "completed";
            const isCurrent = step.status === "current";
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                className={`flex items-start gap-4 ${i < data.steps.length - 1 ? "pb-8" : ""} relative`}>
                {i < data.steps.length - 1 && <div className={`absolute left-5 top-10 w-px h-full ${isCompleted ? "bg-blue-600/30" : "bg-slate-50"}`} />}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 z-10 ${
                  isCompleted ? "bg-blue-600/15 text-blue-600" : isCurrent ? "bg-blue-600 text-white" : "bg-slate-50 text-slate-400"
                }`}>
                  {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <div>
                  <p className={`text-sm font-medium ${isCompleted || isCurrent ? "text-slate-900" : "text-slate-400"}`}>{step.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Step {step.step} of {data.steps.length}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Documents */}
        {data.documents && data.documents.length > 0 && (
          <div className="card-elevated rounded-2xl p-6 mb-8">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Documents</h3>
            {data.documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm text-slate-500">{doc.document_type}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${doc.status === "verified" ? "bg-emerald-50 text-emerald-600" : doc.status === "rejected" ? "bg-red-50 text-red-600" : "bg-blue-50 text-blue-600"}`}>{doc.status}</span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {app.status === "draft" && (
            <>
              <button onClick={() => navigate(`/apply/${id}/documents`)} className="flex-1 py-3 bg-slate-50 border border-slate-200 text-slate-900 rounded-xl text-sm font-medium hover:bg-white/10 transition-all">Upload Documents</button>
              <button onClick={() => navigate(`/apply/${id}/payment`)} className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all">Pay & Submit</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
